<script lang="ts">
  /**
   * Star component for grid intersections and card edges.
   *
   * Variants:
   * - 'full': Complete 4-pointed star (19x19px)
   * - 'top': Top half only (19x9px, bottom clipped)
   * - 'bottom': Bottom half only (19x9px, top clipped)
   * - 'left': Left half only (9x19px, right clipped)
   * - 'right': Right half only (9x19px, left clipped)
   * - 'top-left': Top-left quadrant only (9x9px)
   * - 'top-right': Top-right quadrant only (9x9px)
   * - 'bottom-left': Bottom-left quadrant only (9x9px)
   * - 'bottom-right': Bottom-right quadrant only (9x9px)
   *
   * Usage:
   * <Star variant="full" color="#333335" class="absolute -left-[9px] -top-[9px]" />
   * <Star variant="top" color="#333335" class="absolute left-0 -top-[9px]" />
   * <Star variant="bottom-left" color="#333335" class="absolute -right-px -bottom-px" />
   */

  interface Props {
    variant?: 'full' | 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    color?: string;
    size?: number;
    class?: string;
  }

  let {
    variant = 'full',
    color = '#333335',
    size = 19,
    class: className = ''
  }: Props = $props();

  // Half size for clipping calculations
  const halfSize = $derived(Math.floor(size / 2));

  // Check if quarter variant
  const isQuarter = $derived(variant.includes('-'));

  // Container dimensions based on variant
  const containerWidth = $derived(
    variant === 'left' || variant === 'right' || isQuarter ? halfSize : size
  );
  const containerHeight = $derived(
    variant === 'top' || variant === 'bottom' || isQuarter ? halfSize : size
  );

  // SVG position offset for clipping
  const svgLeft = $derived(
    variant === 'right' || variant === 'top-right' || variant === 'bottom-right' ? -halfSize : 0
  );
  const svgTop = $derived(
    variant === 'bottom' || variant === 'bottom-left' || variant === 'bottom-right' ? -halfSize : 0
  );
</script>

<div
  class="overflow-hidden z-10 {className}"
  style="width: {containerWidth}px; height: {containerHeight}px;"
  aria-hidden="true"
>
  <svg
    width={size}
    height={size}
    viewBox="0 0 19 19"
    fill="none"
    style="position: relative; left: {svgLeft}px; top: {svgTop}px;"
  >
    <path
      d="M9.5 0L12.0659 6.93414L19 9.5L12.0659 12.0659L9.5 19L6.93414 12.0659L0 9.5L6.93414 6.93414L9.5 0Z"
      fill={color}
    />
  </svg>
</div>
